import type { CSSProperties } from 'react';
import { z } from 'zod';
import { TextBloc } from '@/components/TextBlock';
import { TitleColumn } from '@/components/TitleColumn';
import { widgetLayoutSchema } from '@/config/schema';
import { useWidgetData } from '@/hooks/useWidgetData';
import { useWidgetLayout } from '@/hooks/useWidgetLayout';
import { fontOptionsSchema, fontStyleToCss, toCssFontSize } from '@/lib/fontStyle';
import { placeholderText, substituteVariables } from '@/lib/text';
import { warnOnce } from '@/lib/warnOnce';
import {
  cellKind,
  cellValue,
  resolveCapacity,
  type BookingsColumn,
  type CellKind,
} from '@/widgets/BookingsWidget/cells';
import { trackTemplate } from '@/widgets/BookingsWidget/tracks';
import type { WidgetProps } from '@/widgets/registry';

/**
 * A desk-booking grid: one column per weekday, one row per configured area, each cell showing
 * `booked/capacity` in an occupancy colour. Port of `bookings/bookingsWidget.vue`.
 *
 * Used by `szeged_2`, `szeged_3` and `szeged_4`, all three with five weekday columns and four
 * rows, and all three carrying `"slide": 1` — which slide they belong to is SYM-492's concern.
 *
 * ## The unit trap lives here
 *
 * This is the **one** widget whose font sizes arrive already carrying their unit —
 * `"fontSize": "1.2rem"`, not `1.2` — along with `lineHeight`, `width` and `height` in the same
 * pre-united form (docs/MIGRATION-WIDGETS.md §2). `toCssFontSize` is what keeps both forms
 * working: a number gets `rem` appended, a string is passed through. Appending to a string
 * would produce `1.2remrem`, which is invalid CSS — so the declaration is dropped, the text
 * inherits a size that looks entirely plausible on screen, and nothing anywhere says so. It is
 * asserted against the real `szeged_2` values in the test suite for that reason.
 *
 * ## Cell colours beat cell fonts
 *
 * The legacy `.cellLabel` rule sets every font property **except `color`**; the `cell-*` class
 * supplies that, and its background. Keep the precedence or the colour coding disappears into
 * whatever `component.cellLabel.fontColor` says.
 *
 * ## QA-6: the configured widths are intent, not commands
 *
 * `docs/QA-PARITY.md` QA-6. The three deployed configs ask for a 300px row label plus five
 * 200px day cells; with `gap-2` between them that is a 1348px row, laid out inside the 3/4
 * column of a **1080px** screen. 571px of it hung off the right-hand edge, and since nothing
 * scrolls a signage display, **Wednesday, Thursday and Friday were not on the wall** — in this
 * app and, identically, in the legacy one. It has been that way on three displays for as long
 * as they have been up.
 *
 * The widths are config data and config data is immovable (CLAUDE.md constraint 1), so the
 * widget stops treating them as lengths and starts treating them as **proportions**. The table
 * is one CSS grid whose tracks are `minmax(0, <configured>fr)`, so the configured 300:200:200:…
 * ratio is preserved exactly while the row as a whole is always precisely as wide as the space
 * it has — narrower than configured at 1080, wider at 2160, never overflowing at either. Not
 * one byte of any config changes and no new config key is involved.
 *
 * Three consequences, all deliberate:
 *
 *   - **`component.*.width` no longer sizes a box.** It is the numerator of a ratio. Its
 *     *unit* is therefore ignored, which is why mixing units across the three label blocks is a
 *     config mistake rather than a supported case; all three deployed configs use `px` for all
 *     three blocks.
 *   - **The day track is one track.** `columnLabel.width` and `cellLabel.width` describe the
 *     same column, so one of them has to win: the header does, falling back to the cell. They
 *     are both `200px` in every deployed config.
 *   - **The cell wins over its text.** A label that cannot fit its narrowed track is clipped
 *     (`.bookings-grid > .label` in `global.css`) rather than allowed to push the track — which
 *     is what `minmax(0, …)` and `min-width: 0` exist to prevent. Losing the tail of a word is
 *     a great deal better than losing three days of the week.
 *
 * Geometry only: the colour coding, the `value/capacity` content and every font property are
 * untouched.
 */

/** A label block: the shared font options plus the three box properties, all pre-united. */
const labelSchema = fontOptionsSchema.extend({
  lineHeight: z.union([z.number(), z.string()]).optional(),
  width: z.union([z.number(), z.string()]).optional(),
  height: z.union([z.number(), z.string()]).optional(),
});

type BookingsLabel = z.infer<typeof labelSchema>;

/** `options.colors.<kind>` — `text` is the CSS `color`, `bg` the background. */
const cellColorsSchema = z.object({ text: z.string().optional(), bg: z.string().optional() });

const optionsSchema = z.object({
  topic: z.string().optional(),
  layout: widgetLayoutSchema.optional(),
  title: z.string().optional(),
  titleFont: fontOptionsSchema.optional(),
  textBloc: z.object({ text: z.string().default('') }).prefault({}),
  textBlocFont: fontOptionsSchema.optional(),
  /** Column headers, in order. Their count is also the number of cells per row. */
  columns: z.array(z.string()).prefault([]),
  rows: z
    .array(
      z.object({
        /** Indexes the payload's `bookings[column]` and `capacity` maps. */
        key: z.string(),
        /** The label at the start of the row. */
        value: z.string().default(''),
        /** Overrides the payload's capacity for this row — config wins. */
        capacity: z.number().optional(),
      }),
    )
    .prefault([]),
  colors: z
    .object({
      great: cellColorsSchema.prefault({}),
      good: cellColorsSchema.prefault({}),
      poor: cellColorsSchema.prefault({}),
      bad: cellColorsSchema.prefault({}),
      disabled: cellColorsSchema.prefault({}),
    })
    .prefault({}),
  component: z
    .object({
      columnLabel: labelSchema.optional(),
      rowLabel: labelSchema.optional(),
      cellLabel: labelSchema.optional(),
    })
    .prefault({}),
});

/** docs/MIGRATION-WIDGETS.md §7 — `bookings[columnIndex][rowKey]`, plus an optional capacity. */
const payloadSchema = z.looseObject({
  bookings: z.array(z.record(z.string(), z.number())).optional(),
  capacity: z.record(z.string(), z.number()).optional(),
});

type BookingsPayload = z.infer<typeof payloadSchema>;

/** The legacy `.label` rule, shared by all three label kinds. */
const LABEL_BASE: CSSProperties = {
  textAlign: 'center',
  display: 'inline-block',
  verticalAlign: 'middle',
};

/**
 * Legacy defaults, identical across the three label blocks.
 *
 * `width` is not among them any more: it never reaches an element, because it is a grid track
 * ratio now (`tracks.ts`, which carries the legacy `300px` default as `DEFAULT_WIDTH_RATIO`).
 */
const LABEL_DEFAULTS = {
  fontSize: '1.2rem',
  lineHeight: '1rem',
  height: '2rem',
} as const;

export function BookingsWidget({ componentId, options }: WidgetProps) {
  const parsed = optionsSchema.safeParse(options);
  // Hooks run before any early return.
  const opts = parsed.success ? parsed.data : undefined;
  const data = useWidgetData<unknown>(componentId, opts?.topic);
  const { className, style } = useWidgetLayout(opts?.layout);

  if (!parsed.success) {
    warnOnce(
      `bookings-widget-options:${componentId}`,
      '[signage] BookingsWidget options rejected; rendering nothing. %s',
      z.prettifyError(parsed.error),
    );
    return null;
  }

  const opt = parsed.data;
  const payload = readPayload(data, componentId);

  // Neither of these touches the payload object: `resolveCapacity` copies before overriding
  // (the legacy computed wrote into `data.capacity` itself) and `bookings` is only read.
  const capacity = resolveCapacity(opt.rows, payload?.capacity);
  const bookings: BookingsColumn[] = payload?.bookings ?? [];

  const columnStyle: CSSProperties = { ...LABEL_BASE, ...labelStyle(opt.component.columnLabel) };
  const rowStyle: CSSProperties = {
    ...LABEL_BASE,
    ...labelStyle(opt.component.rowLabel),
    textAlign: 'right',
  };
  const cellStyle: CSSProperties = { ...LABEL_BASE, ...labelStyle(opt.component.cellLabel) };

  return (
    <div className={className} style={style}>
      <div className="w-full flex justify-between">
        <TitleColumn className="w-1/4" title={opt.title} titleFont={opt.titleFont}>
          <TextBloc text={resolveText(opt.textBloc.text, payload)} font={opt.textBlocFont} />
        </TitleColumn>
        <div className="w-3/4 flex flex-col flex-wrap justify-end gap-2">
          {/*
           * One grid for the whole table rather than one flex row per config row. The columns
           * then align by construction instead of by every row agreeing on the same widths,
           * which is what made the configured pixel widths load-bearing in the first place.
           * `gap: 0.5rem` is the `gap-2` those flex rows carried, in both directions.
           */}
          <div
            className="bookings-grid gap-2"
            style={{ gridTemplateColumns: trackTemplate(opt.component, opt.columns.length) }}
          >
            {/* The spacer above the row labels — empty, and in the row-label track. */}
            <span className="label columnLabel" style={columnStyle} />
            {opt.columns.map((column, index) => (
              <span key={index} className="label columnLabel text-center" style={columnStyle}>
                {column}
              </span>
            ))}
            {opt.rows.map((row) =>
              [
                <span key={`${row.key}-label`} className="label rowLabel" style={rowStyle}>
                  {row.value}
                </span>,
                ...opt.columns.map((_, columnIndex) => {
                  const column = bookings[columnIndex];
                  const cap = capacity[row.key];
                  const kind = cellKind(column, row.key, cap);
                  return (
                    <span
                      key={`${row.key}-${String(columnIndex)}`}
                      className={`label cellLabel cell-${kind}`}
                      // The `cell-*` colours come last, so they win over `cellLabel.fontColor`
                      // exactly as the legacy stylesheet's omitted `color` did.
                      style={{ ...cellStyle, ...cellColors(opt.colors, kind) }}
                    >
                      {cellValue(column, row.key, cap)}
                    </span>
                  );
                }),
              ].flat(),
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * One label block -> CSS. The size-shaped properties go through `toCssFontSize`, so each
 * accepts the pre-united strings this widget's configs carry *and* the bare numbers every other
 * widget uses.
 *
 * `width` is absent by design: it is a grid track now ({@link trackTemplate}), and writing it
 * here as well would put the fixed pixel width straight back onto the element the track is
 * supposed to be sizing. `height` stays — it is vertical, it is `4rem` in every deployed config,
 * and vertical is what the fit-to-viewport scale handles.
 */
function labelStyle(label: BookingsLabel | undefined): CSSProperties {
  return {
    ...fontStyleToCss(label, { fontSize: LABEL_DEFAULTS.fontSize }),
    lineHeight: toCssFontSize(label?.lineHeight) ?? LABEL_DEFAULTS.lineHeight,
    height: toCssFontSize(label?.height) ?? LABEL_DEFAULTS.height,
  };
}

/** `options.colors.<kind>` as a style fragment. A colour a config omits simply inherits. */
function cellColors(
  colors: z.infer<typeof optionsSchema>['colors'],
  kind: CellKind,
): CSSProperties {
  return { color: colors[kind].text, backgroundColor: colors[kind].bg };
}

/**
 * The text block, in `'strip'` mode (docs/MIGRATION-WIDGETS.md §3): an unresolved
 * `{{variable}}` keeps its name and loses its braces, and before the first message every
 * variable renders as `0`.
 *
 * The legacy component computes only the placeholder form and never updates it — it has no
 * `watch` — so a config with a variable here would show `0` forever. Substituting is what §3
 * specifies for this widget and what the sibling `'strip'` widgets do; none of the three
 * deployed configs puts a variable in this text, so no display renders differently either way.
 */
function resolveText(text: string, payload: BookingsPayload | undefined): string {
  if (payload === undefined) return placeholderText(text);
  return substituteVariables(text, payload, { missing: 'strip' });
}

/** The payload, or `undefined` before the first message / on a shape mismatch. */
function readPayload(data: unknown, componentId: string): BookingsPayload | undefined {
  if (data === undefined) return undefined;

  const parsed = payloadSchema.safeParse(data);
  if (!parsed.success) {
    warnOnce(
      `bookings-widget-payload:${componentId}`,
      '[signage] BookingsWidget payload rejected; keeping the empty grid. %s',
      z.prettifyError(parsed.error),
    );
    return undefined;
  }
  return parsed.data;
}
